import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, BookOpen, User, Phone, Calendar } from '@phosphor-icons/react';

const fmt = (n) => `\u20B9${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

/**
 * Drill-down dialog for the Debtors / Creditors system aggregate cards on the
 * Balance Sheet. Has two modes:
 *
 *  • `list`   — per-customer table with name / phone / days / amount.
 *               Each row links to the same customer's ledger (mode-switch
 *               in-place; no nested dialog).
 *  • `ledger` — full chronological invoice + receipt + advance ledger for the
 *               picked customer. Back arrow returns to `list`.
 *
 * Driven entirely off `/finance/debtors`, `/finance/creditors` and
 * `/customers/{id}/ledger` — no local state writes.
 */
export default function SystemAccountDialog({ kind, open, onOpenChange }) {
  // kind ∈ {'debtors', 'creditors'}.  null when closed.
  const [mode, setMode] = useState('list');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null); // {customer_id, customer_name}
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Reset on close so re-opening always starts at the list view.
  useEffect(() => {
    if (!open) {
      setMode('list');
      setPicked(null);
      setLedger([]);
    }
  }, [open]);

  // Initial load — list view.
  useEffect(() => {
    if (!open || !kind) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/finance/${kind}`)
      .then(r => { if (!cancelled) setRows(r.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, kind]);

  const openLedger = async (row) => {
    setPicked(row);
    setMode('ledger');
    setLedgerLoading(true);
    try {
      const r = await api.get(`/customers/${row.customer_id}/ledger`);
      setLedger(r.data || []);
    } catch {
      setLedger([]);
    } finally {
      setLedgerLoading(false);
    }
  };

  // Aggregates for the list-view footer.
  const totalAmount = rows.reduce((s, r) => s + (r.amount || 0), 0);

  // Aggregates for the ledger-view footer (mirrors CustomersPage logic).
  const ledgerDue = ledger.reduce((s, e) => s + (e.debit || 0) - (e.credit || 0), 0);

  const isDebtors = kind === 'debtors';
  const titleLabel = isDebtors ? 'Customer Dues' : 'Customer Adv.';
  const subLabel = isDebtors ? 'Customers who owe you (billed − received)' : 'Customers holding credit with you (received − billed)';
  const amountLabel = isDebtors ? 'Outstanding' : 'Advance Held';
  const daysLabel = isDebtors ? 'Days Due' : 'Days Held';
  const daysKey = isDebtors ? 'days_due' : 'days_held';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="system-account-dialog">
        <DialogHeader>
          {mode === 'list' ? (
            <>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <BookOpen size={20} weight="duotone" className={isDebtors ? 'text-[#3B82F6]' : 'text-[#7C3AED]'} />
                {titleLabel}
              </DialogTitle>
              <DialogDescription>{subLabel}. Click any name to open the full ledger.</DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setMode('list'); setPicked(null); }} data-testid="back-to-list-btn">
                  <ArrowLeft size={14} className="mr-1" /> Back
                </Button>
                <BookOpen size={20} weight="duotone" className="text-[#10B981]" />
                {picked?.customer_name} — Full Ledger
              </DialogTitle>
              <DialogDescription>
                {picked?.phone && <span className="inline-flex items-center gap-1"><Phone size={12} />{picked.phone}</span>}
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        {/* ------------------------- LIST MODE ------------------------- */}
        {mode === 'list' && (
          loading ? (
            <p className="p-6 text-sm text-[#52525B] text-center">Loading…</p>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm text-[#52525B]">No {titleLabel.toLowerCase()} right now.</p>
              <p className="text-xs text-[#A1A1AA] mt-1">{isDebtors ? 'All customers are paid up.' : 'No active customer advances on the books.'}</p>
            </div>
          ) : (
            <Table data-testid={`${kind}-table`}>
              <TableHeader>
                <TableRow className="border-[#E4E4E7] bg-[#FAFAFA]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Phone</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center">{daysLabel}</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">{amountLabel}</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.customer_id} className="border-[#E4E4E7]">
                    <TableCell>
                      <button
                        onClick={() => openLedger(r)}
                        className="text-sm font-medium text-[#3B82F6] hover:underline text-left flex items-center gap-1.5"
                        data-testid={`open-ledger-${r.customer_id}`}
                      >
                        <User size={13} weight="duotone" />
                        {r.customer_name}
                      </button>
                    </TableCell>
                    <TableCell className="text-xs text-[#52525B]">{r.phone || '—'}</TableCell>
                    <TableCell className="text-center">
                      <Badge
                        className={`rounded-md text-[10px] ${(r[daysKey] || 0) > 30 ? 'bg-[#FEE2E2] text-[#EF4444]' : (r[daysKey] || 0) > 7 ? 'bg-[#FEF3C7] text-[#D97706]' : 'bg-[#E0E7FF] text-[#4F46E5]'}`}
                        variant="outline"
                      >
                        {r[daysKey] || 0}d
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-sm text-right font-mono font-semibold ${isDebtors ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{fmt(r.amount)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={() => openLedger(r)} data-testid={`view-ledger-${r.customer_id}`}>
                        <BookOpen size={12} className="mr-1" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-[#E4E4E7] bg-[#FAFAFA]">
                  <TableCell className="text-sm font-bold" colSpan={3}>Total ({rows.length} customers)</TableCell>
                  <TableCell className={`text-sm text-right font-mono font-bold ${isDebtors ? 'text-[#EF4444]' : 'text-[#10B981]'}`} data-testid={`${kind}-total`}>{fmt(totalAmount)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          )
        )}

        {/* ------------------------- LEDGER MODE ----------------------- */}
        {mode === 'ledger' && (
          ledgerLoading ? (
            <p className="p-6 text-sm text-[#52525B] text-center">Loading ledger…</p>
          ) : ledger.length === 0 ? (
            <p className="p-6 text-sm text-[#52525B] text-center">No transactions for this customer yet.</p>
          ) : (
            <>
              <Table data-testid="customer-ledger-table">
                <TableHeader>
                  <TableRow className="border-[#E4E4E7] bg-[#FAFAFA]">
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Date</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Type</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Description</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Debit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Credit</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledger.map((e, i) => (
                    <TableRow key={i} className="border-[#E4E4E7]">
                      <TableCell className="text-xs text-[#52525B] whitespace-nowrap"><Calendar size={11} className="inline mr-1" />{fmtDate(e.date)}</TableCell>
                      <TableCell><Badge className="rounded-md text-[10px] capitalize" variant="outline">{(e.type || '').replace('_', ' ')}</Badge></TableCell>
                      <TableCell className="text-xs">{e.description}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{e.debit ? fmt(e.debit) : '—'}</TableCell>
                      <TableCell className="text-xs text-right font-mono text-[#10B981]">{e.credit ? fmt(e.credit) : '—'}</TableCell>
                      <TableCell className={`text-xs text-right font-mono font-semibold ${(e.balance ?? 0) > 0 ? 'text-[#EF4444]' : (e.balance ?? 0) < 0 ? 'text-[#10B981]' : ''}`}>{fmt(e.balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-[#E4E4E7] bg-[#FAFAFA]">
                    <TableCell className="text-sm font-bold" colSpan={5}>Net Balance</TableCell>
                    <TableCell className={`text-sm text-right font-mono font-bold ${ledgerDue > 0 ? 'text-[#EF4444]' : ledgerDue < 0 ? 'text-[#10B981]' : ''}`} data-testid="ledger-net">
                      {fmt(ledgerDue)} {ledgerDue > 0 ? '(due)' : ledgerDue < 0 ? '(advance)' : ''}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
